import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Mail, Search, CheckCircle2, XCircle } from "lucide-react";
import { fmtDateTime } from "@/pages/AdminAccessLogs";

export default function AdminEmailLogs() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [success, setSuccess] = useState("all");
  const [days, setDays] = useState("30");
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = { days, limit: 1000 };
    if (q.trim()) params.to = q.trim();
    if (success !== "all") params.success = success;
    api.get("/admin/email-logs", { params }).then((r) => setLogs(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [q, success, days]);
  useEffect(() => { load(); }, [success, days]); // eslint-disable-line

  const okCount = logs.filter((l) => l.success).length;

  return (
    <div className="space-y-6" data-testid="admin-email-logs-page">
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Comunicazioni</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><Mail className="w-7 h-7 text-primary" /> Email inviate</h1>
        <p className="text-sm text-muted-foreground mt-1">Tutte le email/notifiche inviate ai clienti e agli utenti: destinatario, oggetto ed esito.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Cerca per destinatario (email)…" className="pl-9" data-testid="email-search-input" />
        </div>
        <Button variant="outline" onClick={load} data-testid="email-search-button">Cerca</Button>
        <Select value={success} onValueChange={setSuccess}>
          <SelectTrigger className="h-9 w-40" data-testid="email-success-filter"><SelectValue placeholder="Esito" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli esiti</SelectItem><SelectItem value="true">Consegnate</SelectItem><SelectItem value="false">Non inviate</SelectItem></SelectContent>
        </Select>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="h-9 w-36" data-testid="email-days-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="1">Oggi</SelectItem><SelectItem value="7">7 giorni</SelectItem><SelectItem value="30">30 giorni</SelectItem><SelectItem value="90">90 giorni</SelectItem><SelectItem value="180">180 giorni</SelectItem></SelectContent>
        </Select>
        <Badge variant="outline" className="ml-auto" data-testid="email-count-badge">{logs.length} email · {okCount} ok</Badge>
      </div>

      <Card className="glow-border">
        <Table>
          <TableHeader><TableRow><TableHead>Data e ora</TableHead><TableHead>Destinatario</TableHead><TableHead>Oggetto</TableHead><TableHead className="text-right">Esito</TableHead></TableRow></TableHeader>
          <TableBody>
            {logs.map((l) => (
              <TableRow key={l.id} data-testid={`email-row-${l.id}`}>
                <TableCell className="whitespace-nowrap text-sm">{fmtDateTime(l.at)}</TableCell>
                <TableCell className="text-sm">{l.to}</TableCell>
                <TableCell className="text-sm">{l.subject}</TableCell>
                <TableCell className="text-right">{l.success
                  ? <Badge className="bg-teal-600 hover:bg-teal-600 gap-1"><CheckCircle2 className="w-3 h-3" /> Inviata</Badge>
                  : <Badge className="bg-red-500 hover:bg-red-500 gap-1"><XCircle className="w-3 h-3" /> Non inviata</Badge>}</TableCell>
              </TableRow>
            ))}
            {logs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">{loading ? "Caricamento…" : "Nessuna email nel periodo selezionato."}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
